import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SCROLL_OPTIONS = { top: 0, left: 0, behavior: 'auto' };

const ScrollManager = () => {
  const { pathname, hash, key } = useLocation();

  // On initial mount, disable browser's scroll restoration and force top (unless there is a hash)
  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }
    } catch {}

    if (typeof window !== 'undefined' && !window.location.hash) {
      window.scrollTo(SCROLL_OPTIONS);
      // Ensure top after first paint/layout as a fallback against late content shifts
      const t = setTimeout(() => {
        try { window.scrollTo(0, 0); } catch {}
      }, 0);
      return () => clearTimeout(t);
    }
  }, []);

  // Also react to manual hash edits in the URL bar
  useEffect(() => {
    const onHash = () => {
      const id = (window.location.hash || '').replace('#', '');
      if (!id) return;
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      if (!id) return;

      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return true;
        }
        return false;
      };

      if (tryScroll()) return;

      // Retry a few times to allow lazy content to mount
      let tries = 0;
      const maxTries = 30; // ~3s at 100ms
      const tick = () => {
        if (tryScroll()) return;
        tries += 1;
        if (tries < maxTries) {
          setTimeout(tick, 100);
        }
      };
      const timeout = setTimeout(tick, 150);
      return () => clearTimeout(timeout);
    }

    // No hash: always ensure top on route changes
    window.scrollTo(SCROLL_OPTIONS);
  }, [pathname, hash, key]);

  return null;
};

export default ScrollManager;
